import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { asFileKind } from '@/lib/fileKinds'
import { queryKeys } from '@/lib/query'
import { MOCK_CASE_DETAIL } from '@/mocks/cases'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CaseFile } from '@/types/domain'

/* A case's files (Story 8.1, FR-45, AD-18).
 *
 * The upload is three requests and the middle one does not come here: the
 * browser PUTs the bytes STRAIGHT to object storage, and they never pass
 * through the application. That is the whole point of the pre-signed URL — a
 * reloading photo from a bonded warehouse travels once, over a connection that
 * drops, instead of twice.
 *
 *   1. POST /cases/:id/files/upload-url  → a ticket: URL, key, expiry, max size
 *   2. PUT  <the ticket's URL>           → bytes, direct to the bucket
 *   3. POST /cases/:id/files             → metadata, once the bytes have landed
 *
 * Step 3 is what creates the row. If step 2 fails there is nothing to clean up:
 * no database row was ever written, and the orphaned object (if any) is swept
 * by storage versioning rather than by us.
 *
 * SENDING lives in useUploadQueue (Story 8.2), which drives those three steps
 * durably. What remains here is reading: the list, and the download link.
 */

/** What the server serves for one file. Deliberately WITHOUT the object key:
 *  downloads are short-lived signed URLs issued per request (AD-18). */
interface FileResponse {
  id: string
  fileName: string
  contentType: string
  /** The document kind (0045). Always present — the column is NOT NULL — but
   *  typed optional so a response from a build before 0045 still parses. */
  docKind?: string
  byteSize: number
  uploadedById: string
  uploadedByName: string
  uploadedAt: string
}

interface DownloadResponse {
  url: string
  fileName: string
  expiresAt: string
}

/** The wire shape mapped to the domain type the components already render. */
function toCaseFile(file: FileResponse): CaseFile {
  return {
    id: file.id,
    name: file.fileName,
    sizeBytes: file.byteSize,
    kind: file.contentType,
    // Narrowed rather than cast: a code this build does not know renders as
    // "Other" instead of taking the file area down, which is what an escape
    // hatch is for.
    docKind: asFileKind(file.docKind),
    uploadedByName: file.uploadedByName,
    uploadedAt: file.uploadedAt,
  }
}

export function useCaseFiles(caseId: string) {
  return useQuery<CaseFile[]>({
    queryKey: queryKeys.cases.files(caseId),
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_CASE_DETAIL[caseId]?.files ?? []
      const { items } = await api.get<{ items: FileResponse[] }>(
        `/cases/${caseId}/files`,
      )
      return items.map(toCaseFile)
    },
  })
}

/* The direct, non-durable upload that used to live here is GONE (Story 8.2).
 *
 * Every upload now goes through the durable queue in
 * features/cases/api/useUploadQueue, which persists the file before sending it
 * and carries an idempotency key on every attempt. Keeping this hook beside
 * that one would have meant two ways to upload with different guarantees, and
 * the one a user reached by dropping a file rather than pressing a button
 * would have been the one with no queue behind it — an inconsistency nobody
 * could predict from the interface. UploadTicket, which described the signing
 * step, moved there with it.
 */

/** Removes a file from the case (FR-45).
 *
 *  NOT OPTIMISTIC, and that is the choice worth naming for a delete: an
 *  optimistic list drops the row before the server agrees, and if the request
 *  then fails the row comes back — a file that vanished and reappeared reads as
 *  a bug in exactly the moment a user needs to trust what they are looking at.
 *  A delete is also the one act here that cannot be undone, so the screen should
 *  say it happened only once it has.
 *
 *  Keyed by file id alone, because the endpoint is: a path carrying the case as
 *  well would let a caller pair a reachable case with somebody else's document.
 *  The case id is passed only to know which list to invalidate. */
export function useDeleteCaseFile(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (fileId: string) => {
      if (USE_MOCKS) return
      await api.delete(`/files/${fileId}`)
    },
    onSuccess: () => {
      if (USE_MOCKS) return
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.files(caseId) })
    },
  })
}

/** Opens a file (AD-18).
 *
 *  Fetches a fresh signed URL every time rather than caching one: the link
 *  lives five minutes, and a cached one would fail for the user who left the
 *  case open over lunch. Getting a new URL costs one request and always works.
 */
export function useDownloadCaseFile() {
  return useMutation({
    mutationFn: async (fileId: string): Promise<DownloadResponse> => {
      if (USE_MOCKS) {
        return { url: '#', fileName: 'mock', expiresAt: new Date().toISOString() }
      }
      return api.get<DownloadResponse>(`/files/${fileId}/download`)
    },

    onSuccess: (download) => {
      if (USE_MOCKS) return
      // A new tab rather than a navigation: the case stays open behind it, and
      // the browser streams from the bucket rather than through our server.
      window.open(download.url, '_blank', 'noopener,noreferrer')
    },
  })
}
